<script setup lang="ts">
import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/vue-query';
import { marked } from 'marked';
import { ref } from 'vue';

import {
  markNotificationAsReadMutation,
  type Notification,
  notificationsQueries,
  useNotifications,
} from '@/entities/notification';
import { formatDateTime } from '@/shared/lib/date';
import { Badge } from '@/shared/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import { Spinner } from '@/shared/ui/spinner';

const open = defineModel<boolean>('open');

const queryClient = useQueryClient();
const page = ref(1);
const query = ref('');

const currentMutateNotificationId = ref<number | undefined>();

const { data: notifications } = useNotifications(
  { page: page.value, query: query.value },
  {
    placeholderData: keepPreviousData,
  },
);

const { mutate: read, isPending: isDismissing } = useMutation({
  ...markNotificationAsReadMutation(),
  onSettled: () => {
    queryClient.invalidateQueries({
      queryKey: notificationsQueries.list({ query: query.value, page: page.value }),
    });
  },
});

function dismiss(notification: Notification) {
  currentMutateNotificationId.value = notification.id;
  read(notification.id);
}
</script>

<template>
  <Sheet v-model:open="open">
    <SheetContent>
      <SheetHeader class="gap-y-1">
        <SheetTitle>Notifications</SheetTitle>
        <SheetDescription> Stay up to date with the latest news and updates. </SheetDescription>
      </SheetHeader>
      <template v-if="notifications">
        <div v-if="notifications.length > 0" class="flex-1 divide-y overflow-y-auto px-4">
          <div v-for="notification in notifications" :key="notification.id" class="py-4">
            <div class="mb-2 space-x-1">
              <Badge
                class="inline"
                :variant="
                  notification.viewed_by_user
                    ? 'outline'
                    : notification.important
                      ? 'destructive'
                      : 'default'
                "
                >News</Badge
              >
              <span class="font-medium">{{ notification.title }}</span>
            </div>
            <div
              class="prose prose-neutral prose-sm dark:prose-invert prose-headings:font-medium prose-strong:font-medium text-muted-foreground line-clamp-3 italic"
              v-html="marked(notification.text)"
            ></div>
            <div class="mt-4 flex items-center justify-between text-xs">
              <time class="font-medium">{{ formatDateTime(notification.created_at) }}</time>
              <span v-if="notification.viewed_by_user" class="text-muted-foreground"
                >Dismissed</span
              >
              <button v-else class="text-primary font-medium" @click="dismiss(notification)">
                {{
                  currentMutateNotificationId &&
                  currentMutateNotificationId === notification.id &&
                  isDismissing
                    ? 'Dismissing...'
                    : 'Dismiss'
                }}
              </button>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center pt-16">
          <img
            src="@/assets/states/notifications-bell.svg"
            alt="No notifications"
            class="mx-auto mb-3 h-32"
          />
          <h3 class="text-center font-medium">Your notifications will appear here.</h3>
        </div>
      </template>
      <div v-else class="flex items-center justify-center pt-32">
        <Spinner />
      </div>
    </SheetContent>
  </Sheet>
</template>
